import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { AlertTriangle, Loader2, Trash2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const DELETE_PHRASE = 'CANCELLA DEFINITIVAMENTE';

/**
 * Mandatory confirmation modal for definitive record deletion.
 * The admin must type the exact phrase before the delete button activates.
 * Every deletion is audit-logged server-side (admin_audit_log).
 */
export const DeleteConfirmModal = ({ target, token, onClose, onDeleted }) => {
  const [phrase, setPhrase] = useState('');
  const [busy, setBusy] = useState(false);

  const close = () => {
    setPhrase('');
    onClose();
  };

  const doDelete = async () => {
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/admin/records/delete`,
        { collection: target.collection, ids: [target.id], confirm: DELETE_PHRASE },
        { headers: { 'X-Admin-Token': token } }
      );
      toast.success(`Record eliminato definitivamente · audit ${String(r.data.audit_id).slice(0, 8)}…`);
      setPhrase('');
      onDeleted();
    } catch {
      toast.error('Eliminazione fallita');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!target} onOpenChange={(v) => !v && close()}>
      <DialogContent
        className="max-w-md bg-white rounded-2xl border border-red-200 p-6"
        data-testid="pv-admin-delete-modal"
      >
        {target && (
          <>
            <DialogHeader className="text-left">
              <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-red-50 border border-red-200 mb-2">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </span>
              <DialogTitle className="font-heading font-bold text-[19px] text-pv-ink">
                Cancellazione definitiva
              </DialogTitle>
              <DialogDescription className="text-[13px] text-slate-600 leading-relaxed">
                Stai per eliminare <strong className="text-red-600">in modo irreversibile</strong> il record:
                <span className="block mt-1.5 font-semibold text-pv-ink" data-testid="pv-admin-delete-label">
                  {target.label}
                </span>
                <span className="block mt-1 font-mono text-[11px] text-slate-400">
                  {target.collectionLabel || target.collection} · ID {String(target.id).slice(0, 8)}…
                </span>
              </DialogDescription>
            </DialogHeader>
            <div className="mt-1">
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">
                Digita <span className="text-red-600 font-black">{DELETE_PHRASE}</span> per confermare
              </label>
              <input
                value={phrase}
                onChange={(e) => setPhrase(e.target.value)}
                placeholder={DELETE_PHRASE}
                data-testid="pv-admin-delete-phrase"
                className="pv-input mt-2 !border-red-300 focus:!border-red-500"
                disabled={busy}
              />
            </div>
            <div className="mt-4 flex items-center justify-end gap-2">
              <button
                onClick={close}
                className="pv-btn-ghost !py-2.5 !px-4 !text-[13px]"
                data-testid="pv-admin-delete-cancel"
              >
                Annulla
              </button>
              <button
                onClick={doDelete}
                disabled={busy || phrase !== DELETE_PHRASE}
                data-testid="pv-admin-delete-execute"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 text-white text-[13px] font-bold disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                Elimina definitivamente
              </button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DeleteConfirmModal;
